import { Injectable, OnDestroy, signal, Signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ParamsConfig, paramsConfig } from './models/params.config';
import { startWith, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ParamsService implements OnDestroy {

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) { }

  private readonly _params: WritableSignal<Partial<ParamsConfig>> = signal({});

  private routeSubscription?: Subscription;

  public ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
  }

  public get params(): Signal<Partial<ParamsConfig>> {
    return this._params;
  }

  public set params(params: Partial<ParamsConfig>) {
    const currentParams = { ...this.route.snapshot.queryParams };

    const newParams: any = {
      ...currentParams,
      ...params,
    };

    for (const key in newParams) {
      if (paramsConfig.hasOwnProperty(key))
        newParams[key] = this.mapValue(newParams[key]);

      if (paramsConfig.hasOwnProperty(key) && this.isInvalid(newParams[key as keyof ParamsConfig]))
        delete newParams[key];
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: newParams,
    });
  }

  public initialize(): void {
    this.routeSubscription = this.route.queryParams
      .pipe(
        startWith(this.route.snapshot.queryParams)
      )
      .subscribe(queryParams => {
        const params = this.getTypedParams(queryParams);
        this._params.set(params);
      });
  }

  public isInvalid(value: any): boolean {
    return value === undefined || value === null || value === '';
  }

  public amount(ignoreParams?: string[]): number {
    let amount = 0;

    Object.keys(this.params()).forEach(key => {
      if (ignoreParams && ignoreParams.includes(key))
        return;

      const value = this.params()[key as keyof ParamsConfig];
      if (value !== undefined)
        amount++;
    });

    return amount;
  }

  private mapValue(value: any): string | undefined {
    if (typeof value === 'object' && value !== null)
      return JSON.stringify(value);

    if (!value)
      return undefined;

    return String(value);
  }

  private getTypedParams(queryParams: { [key: string]: any }): Partial<ParamsConfig> {
    const typedParams: Partial<ParamsConfig> = {};

    for (const key in queryParams) {
      if (!paramsConfig.hasOwnProperty(key))
        continue;

      // TODO: Prototype pollution
      const functionValidation = paramsConfig[key as keyof typeof paramsConfig];

      const value = queryParams[key];
      const newValue = functionValidation(value);

      if (!newValue)
        continue;

      typedParams[key as keyof ParamsConfig] = newValue;
    }

    return typedParams as Partial<ParamsConfig>;
  }

}

